import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from '../../utils/axios';
import { normalizeGeofence } from '../../utils/geofenceUtils';
import { assignLayers } from '../../utils/polygonLayers';
import { isMissingAddressAutosaveError } from '../../utils/listingErrors';
import { GEOFENCE_AUTOSAVE_FAILED } from '../../utils/geofenceMessages';
import { toastError } from '../../utils/toast';
import GeofenceEditor from '../../pages/OnboardingWizard/GeofenceEditor';
import GeofenceToolbar from '../GeofenceToolbar/GeofenceToolbar';
import { useAiBoundaryDecision } from '../../pages/OnboardingWizard/useAiBoundaryDecision';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import './GeofenceDrawModal.css';

export default function GeofenceDrawModal({
    open,
    onClose,
    listingId,
    initialGeofence,
    initialIngress = [],
    initialEgress = [],
    initialEnrichmentFields = null,
    addressData,
    hasAddress = true,
    onSaved,
    skipAutoQuery = false,
    errorBannerSlot = null,
}) {
    const [autosaveStatus, setAutosaveStatus] = useState('idle');
    const [regenLoading, setRegenLoading] = useState(false);
    const [activeTool, setActiveTool] = useState('drag');
    const [activeLayer, setActiveLayer] = useState('all');
    const [currentPolygons, setCurrentPolygons] = useState(() => normalizeGeofence(initialGeofence));
    // Bump each time the modal transitions closed→open so the copy-decision
    // hook resets. Combined with GeofenceEditor's onOsmResult signal below,
    // this yields the definitive answer instead of guessing with a timer.
    const [openInstance, setOpenInstance] = useState(0);
    const { hasAiBoundary: hadAiBoundary, signalAiBoundary } = useAiBoundaryDecision({ resetKey: openInstance });
    const idleTimerRef = useRef(null);
    const regenFnRef = useRef(null);
    const enrichmentFieldsRef = useRef(initialEnrichmentFields);
    const requestSeqRef = useRef(0);
    const prevOpenRef = useRef(false);
    const modalRef = useRef(null);

    const layers = useMemo(() => assignLayers(currentPolygons), [currentPolygons]);
    const layerCount = useMemo(() => {
        if (!layers.length) return 0;
        return new Set(layers).size;
    }, [layers]);

    useEffect(() => {
        enrichmentFieldsRef.current = initialEnrichmentFields;
    }, [initialEnrichmentFields]);

    useEffect(() => {
        const justOpened = open && !prevOpenRef.current;
        prevOpenRef.current = open;
        if (!justOpened) return;
        setActiveTool('drag');
        setActiveLayer('all');
        setCurrentPolygons(normalizeGeofence(initialGeofence));
        setOpenInstance((n) => n + 1);
    }, [open, initialGeofence]);

    useEffect(() => {
        if (layerCount <= 1 && activeLayer !== 'all') {
            setActiveLayer('all');
        }
    }, [layerCount, activeLayer]);

    useEffect(() => {
        if (!open) {
            setAutosaveStatus('idle');
            clearTimeout(idleTimerRef.current);
        }
    }, [open]);

    useEffect(() => () => clearTimeout(idleTimerRef.current), []);

    const handleRegenUpdate = useCallback((loading, fn) => {
        setRegenLoading(loading);
        regenFnRef.current = fn;
    }, []);

    const handleAutosave = useCallback(async (payload) => {
        const normalized = normalizeGeofence(payload?.geofence);
        const hasGeofence = normalized.some((section) => section.length >= 3);
        const hasIngress = Array.isArray(payload?.ingress) && payload.ingress.length > 0;
        const hasEgress = Array.isArray(payload?.egress) && payload.egress.length > 0;
        const markHostSet = payload?.markIngressEgressHostSet === true;
        if (!listingId || (!hasGeofence && !hasIngress && !hasEgress && !markHostSet)) return;
        if (!hasAddress) return;

        const mySeq = ++requestSeqRef.current;
        setAutosaveStatus('saving');
        clearTimeout(idleTimerRef.current);
        try {
            const body = {};
            if (hasGeofence) body.geofence = payload.geofence;
            if ('ingress' in payload) body.ingress = payload.ingress;
            if ('egress' in payload) body.egress = payload.egress;
            if (markHostSet) {
                const existing = enrichmentFieldsRef.current && typeof enrichmentFieldsRef.current === 'object'
                    ? enrichmentFieldsRef.current
                    : {};
                body.enrichmentFields = {
                    ...existing,
                    ingressEgressMethod: 'host_set',
                    ingressEgressConfidence: 'high',
                };
                enrichmentFieldsRef.current = body.enrichmentFields;
            }
            const res = await axios.put(`/listings/${listingId}`, body);
            if (mySeq !== requestSeqRef.current) return;
            setAutosaveStatus('saved');
            onSaved?.(payload);
            if (res.data?.listing?.enrichmentFields) {
                enrichmentFieldsRef.current = res.data.listing.enrichmentFields;
            }
            idleTimerRef.current = setTimeout(() => setAutosaveStatus('idle'), 2500);
        } catch (err) {
            if (isMissingAddressAutosaveError(err)) return;
            if (mySeq !== requestSeqRef.current) return;
            toastError(GEOFENCE_AUTOSAVE_FAILED);
            setAutosaveStatus('idle');
        }
    }, [listingId, onSaved, hasAddress]);

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) onClose();
    };

    const statusLine = {
        idle: 'Changes save automatically',
        saving: 'Saving…',
        saved: '✓ Saved',
    }[autosaveStatus] ?? 'Changes save automatically';

    useFocusTrap(modalRef, open, onClose);

    if (!open) return null;

    return createPortal(
        <div className="geofence-draw-modal-root">
            <div
                className="geofence-draw-modal-overlay"
                onClick={handleOverlayClick}
                role="presentation"
            >
                <div
                    ref={modalRef}
                    className="geofence-draw-modal"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="geofence-draw-modal-title"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="geofence-draw-modal-head">
                        <div>
                            <h3 id="geofence-draw-modal-title">Edit parking boundary</h3>
                            <div className="geofence-draw-modal-head-sub">{statusLine}</div>
                            {errorBannerSlot}
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                            <button
                                type="button"
                                className="geofence-draw-modal-action"
                                onClick={() => regenFnRef.current?.()}
                                disabled={regenLoading}
                                title="Re-query OpenStreetMap"
                                aria-label="Regenerate boundary"
                            >
                                <svg
                                    width="15"
                                    height="15"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    style={regenLoading ? { animation: 'geofenceDrawModalSpin 0.8s linear infinite' } : undefined}
                                >
                                    <path d="M23 4v6h-6" />
                                    <path d="M1 20v-6h6" />
                                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                                </svg>
                            </button>
                            <button
                                type="button"
                                className="geofence-draw-modal-close"
                                onClick={onClose}
                                aria-label="Close"
                            >
                                <svg
                                    width="15"
                                    height="15"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.2"
                                    strokeLinecap="round"
                                    aria-hidden
                                >
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    <GeofenceToolbar
                        activeTool={activeTool}
                        onChangeTool={setActiveTool}
                        layerCount={layerCount}
                        activeLayer={activeLayer}
                        onChangeLayer={setActiveLayer}
                        hasAiBoundary={hadAiBoundary}
                    />

                    <div className="geofence-draw-modal-body">
                        <GeofenceEditor
                            embedded
                            listingId={listingId}
                            addressData={addressData}
                            initialPolygon={initialGeofence}
                            initialIngress={initialIngress}
                            initialEgress={initialEgress}
                            activeTool={activeTool}
                            activeLayer={activeLayer}
                            layers={layers}
                            onChangeTool={setActiveTool}
                            onPolygonsChange={setCurrentPolygons}
                            onAutosave={handleAutosave}
                            onRegenUpdate={handleRegenUpdate}
                            onOsmResult={signalAiBoundary}
                            skipAutoQuery={skipAutoQuery}
                        />
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
}
